import type { User } from 'firebase/auth';
import type { LegalAttempt, LegalDocument, NoticeVariant } from './legalTypes';

const PREFIX = 'thea:legal-attempt:';
const REDIRECT = 'thea:legal-redirect';
interface Pending { attempt: LegalAttempt; uid?: string }
const sending = new Set<string>();
// A quota/storage failure AFTER successful authentication must not turn success
// into an auth error. Keep the bound receipt in memory and retry persistence.
const boundInMemory = new Map<string, Pending>();

function read(key: string): Pending | null {
  if (boundInMemory.has(key)) return boundInMemory.get(key)!;
  try { return JSON.parse(localStorage.getItem(key) ?? 'null'); } catch { return null; }
}

/** Synchronous durable write BEFORE invoking a provider, preserving popup activation. */
export function beginLegalAttempt(document: LegalDocument, variant: NoticeVariant, button: LegalAttempt['button']): string {
  const attempt: LegalAttempt = {
    attemptId: crypto.randomUUID(), acceptedDocumentId: document.id,
    screen: variant === 'claim' ? 'claim' : 'signin', button, noticeVariant: variant,
    uiBuildId: process.env.REACT_APP_BUILD_SHA || 'local-development', clientTappedAt: new Date().toISOString(),
  };
  // Storage errors deliberately stop auth before credentials change. No pretend durability.
  localStorage.setItem(PREFIX + attempt.attemptId, JSON.stringify({ attempt }));
  if (button === 'google' || button === 'apple') {
    try { sessionStorage.setItem(REDIRECT, attempt.attemptId); }
    catch (err) { localStorage.removeItem(PREFIX + attempt.attemptId); throw err; }
  }
  return attempt.attemptId;
}

export function cancelLegalAttempt(id?: string) {
  if (!id) return;
  // Never discard a successfully bound attempt because a later UI step threw.
  try {
    if (!read(PREFIX + id)?.uid) localStorage.removeItem(PREFIX + id);
    if (sessionStorage.getItem(REDIRECT) === id) sessionStorage.removeItem(REDIRECT);
  } catch { /* An unbound attempt is never replayed as consent. */ }
}

export function completeLegalAttempt(id: string, user: User) {
  const pending = read(PREFIX + id);
  if (!pending || user.isAnonymous || (pending.uid && pending.uid !== user.uid)) return;
  boundInMemory.set(PREFIX + id, { ...pending, uid: user.uid });
  try {
    localStorage.setItem(PREFIX + id, JSON.stringify({ ...pending, uid: user.uid }));
    boundInMemory.delete(PREFIX + id);
    if (sessionStorage.getItem(REDIRECT) === id) sessionStorage.removeItem(REDIRECT);
  } catch { /* Retry the bound evidence below, without rerunning auth. */ }
  void flushLegalOutbox(user);
}

/** Called only with an actual redirect credential, never with a restored session. */
export function readLegalRedirectAttempt(): string | null {
  try { return sessionStorage.getItem(REDIRECT); } catch { return null; }
}

export function completeLegalRedirect(user: User | null, id: string | null) {
  if (!id) return;
  if (user) completeLegalAttempt(id, user);
  else cancelLegalAttempt(id);
}

export async function flushLegalOutbox(user: User | null) {
  if (!user || user.isAnonymous) return;
  // One key per attempt avoids cross-tab read/modify/write losses.
  let storedKeys: string[] = [];
  try { storedKeys = Object.keys(localStorage).filter(k => k.startsWith(PREFIX)); } catch { /* memory fallback */ }
  for (const key of Array.from(new Set([...storedKeys, ...Array.from(boundInMemory.keys())]))) {
    const pending = read(key);
    if (!pending || pending.uid !== user.uid || sending.has(key)) continue;
    sending.add(key);
    try {
      try {
        localStorage.setItem(key, JSON.stringify(pending));
        boundInMemory.delete(key);
      } catch { boundInMemory.set(key, pending); }
      // Linking a guest must not submit with the pre-upgrade anonymous token.
      await user.getIdToken(true);
      const { recordLegalAgreement } = await import('../callables');
      const { data } = await recordLegalAgreement({ ...pending.attempt, accountUid: user.uid });
      if (data.acceptedDocumentId === pending.attempt.acceptedDocumentId && data.agreementId) {
        boundInMemory.delete(key);
        localStorage.removeItem(key);
      }
    } catch {
      // Preserve through network/service failures. The root retries while this
      // account is signed in; another account can never submit this evidence.
    } finally { sending.delete(key); }
  }
}
